import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Loader2, Tag, Maximize2, MapPin, Clock } from 'lucide-react'
import { KPICard } from '../../ui'
import { useToast } from '../../../contexts/ToastContext'
import AddPartnerDrawer from '../../partners/AddPartnerDrawer'
import broadcastService, { customerReviewSource } from '../../../services/broadcastService'
import { ExclusionReason, ReviewBrand } from '../../../services/broadcastAdminService'
import BroadcastBrandCard from '../BroadcastBrandCard'
import { rateFor } from '../broadcastValuation'
import BroadcastBrandDrawer from '../BroadcastBrandDrawer'
import BroadcastPageHeader from './BroadcastPageHeader'
import BroadcastUnavailable from './BroadcastUnavailable'
import useBroadcastExposure from './useBroadcastExposure'
import { formatDuration } from './broadcastCustomerFormat'
import BroadcastToolbar from '../BroadcastToolbar'
import BroadcastPagination from '../BroadcastPagination'

// 24 = six full rows of the 4-wide grid at xl.
const PER_PAGE = 24

/**
 * One broadcast's UNSPONSORED brands and marks: everything on screen that
 * resolves to none of the customer's partners.
 *
 * Sponsors live on Sponsor Exposure. This page is the rest — leagues, clubs,
 * venues, broadcaster idents and brands nobody has signed — which is both the
 * unsold inventory and the place to spot a sponsor whose name or alias is
 * missing from the partner list. `is_sponsored` is resolved from that list on
 * every read, so adding the partner moves the brand across with no edit here.
 *
 * The headline measure is AREA-SECONDS, not seconds. A board that fills a
 * tenth of the screen for ten seconds and a board that fills a hundredth for
 * the same ten seconds are not worth the same, and raw duration says they are.
 *
 * Exposure is NOT expressed in EMV here. Social EMV and broadcast exposure do
 * not yet share a unit, and inventing a conversion to make one table would be
 * asserting a rate nobody has agreed.
 *
 * It IS expressed in money, which is a different claim: the broadcast's own
 * 30-second rate, from its own audience and the rate card someone assigned
 * to it. That is a figure the customer supplied, not one we invented.
 *
 * Location and readability breakdowns live on the details page — they describe
 * the broadcast, not any one brand.
 */
const BroadcastBrandsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const { showError, showSuccess } = useToast()
  // The brand being turned into a sponsor. Set from the brand drawer, which
  // closes so the two drawers never stack.
  const [sponsorFrom, setSponsorFrom] = useState<ReviewBrand | null>(null)
  const { customerId, data, loading, locked, reload } = useBroadcastExposure(id)
  const [busy, setBusy] = useState(false)
  const [drilled, setDrilled] = useState<ReviewBrand | null>(null)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [refreshing, setRefreshing] = useState(false)
  // Reviewer exclusions are hidden by default — they are already decided —
  // but the toggle has to exist, because hiding them outright would strand
  // the decision to restore one. NOT keyed on `counts`: most unsponsored
  // marks never count, so that filter would empty the page.
  const [hideExcluded, setHideExcluded] = useState(true)

  const refresh = async () => {
    setRefreshing(true)
    try { await reload() } finally { setRefreshing(false) }
  }

  useEffect(() => { setPage(1) }, [search, hideExcluded])

  const allBrands = data?.brands ?? []
  const brands = useMemo(() => allBrands.filter((b) => !b.is_sponsored), [allBrands])
  const hiddenCount = brands.filter((b) => b.excluded_reason !== null).length
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase()
    const base = hideExcluded ? brands.filter((b) => b.excluded_reason === null) : brands
    return q ? base.filter((b) =>
      (b.display_name ?? b.brand_name).toLowerCase().includes(q) ||
      b.brand_name.toLowerCase().includes(q)) : base
  }, [brands, search, hideExcluded])
  const pageBrands = useMemo(
    () => shown.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [shown, page]
  )

  const toggleExclude = async (brandName: string, reason: ExclusionReason | null) => {
    if (!id) return
    setBusy(true)
    try {
      await broadcastService.setBrandExclusion(customerId, id, brandName, reason)
      await reload()
    } catch (err) {
      showError('Could not update', err instanceof Error ? err.message : 'Please try again')
    } finally { setBusy(false) }
  }

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-gray-500">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
    </div>
  }

  if (!data) return <BroadcastUnavailable locked={locked} />

  const { broadcast: b } = data
  // Reviewer-excluded rows ship so they can be restored, so the totals filter
  // rather than sum the array. Locations come from these rows, not the
  // payload's `zones`, which is sponsored-only.
  const active = brands.filter((x) => x.excluded_reason === null)
  const totalArea = active.reduce((s, x) => s + Number(x.total_area_seconds), 0)
  const locations = new Set(
    active.flatMap((x) => (x.zones ?? '').split(',').filter(Boolean))
  ).size

  return (
    <div className="p-6 space-y-5 max-w-5xl">
      <BroadcastPageHeader broadcast={b} view="brands" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          label="Unsponsored Brands" value={active.length.toLocaleString()}
          icon={Tag} colorScheme="blue"
          tooltip="Brands and marks on screen that match none of your partners — leagues, clubs, venues, broadcasters and unsigned brands. Excludes anything set aside in review."
        />
        <KPICard
          label="Unsponsored Area-Seconds" value={totalArea.toFixed(1)}
          icon={Maximize2} colorScheme="emerald"
          tooltip="Screen time weighted by how much of the screen was filled, for brands no partner is paying for."
        />
        <KPICard
          label="Placements" value={locations.toLocaleString()}
          icon={MapPin} colorScheme="purple"
          tooltip="Distinct placements where unsponsored brands appeared, such as the backstop boards or the outfield fence."
        />
        <KPICard
          label="Broadcast Length" value={formatDuration(b.duration_ms)}
          icon={Clock} colorScheme="amber"
        />
      </div>

      <div>
        <div className="mb-3">
          <h2 className="text-sm font-semibold text-gray-900">Unsponsored brands by exposure</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Everything on screen that matches none of your sponsors. If one of these is a sponsor,
            add it to your sponsors (or as an alias) and it moves to Sponsor Exposure.
          </p>
        </div>

        {brands.length > 0 && (
          <div className="mb-3">
            <BroadcastToolbar
              value={search}
              onChange={setSearch}
              placeholder="Search brands..."
              onRefresh={() => { void refresh() }}
              refreshing={refreshing}
              refreshLabel="Refresh brands"
            >
              {hiddenCount > 0 && (
                <button
                  onClick={() => setHideExcluded((v) => !v)}
                  className={`inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors ${
                    hideExcluded
                      ? 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      : 'bg-blue-50 text-blue-700 border-blue-200'
                  }`}
                >
                  {hideExcluded ? `Show ${hiddenCount} excluded` : 'Hide excluded'}
                </button>
              )}
            </BroadcastToolbar>
          </div>
        )}
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {pageBrands.map((x) => (
            <BroadcastBrandCard
              key={x.brand_key}
              brand={x}
              customerId={customerId}
              broadcastId={id!}
              // Same rate for every card on the page, derived once from the
              // broadcast. The card renders a dash when it is null, which is
              // the ordinary state until support record an audience.
              rate={rateFor(b)}
              disabled={busy}
              source={customerReviewSource}
              onOpen={() => setDrilled(x)}
              onExclude={(reason) => toggleExclude(x.brand_name, reason as ExclusionReason | null)}
            />
          ))}
        </div>
        {brands.length === 0 && (
          <div className="bg-white rounded-lg border border-gray-200 py-10 text-center text-sm text-gray-400">
            Every brand on this broadcast matches one of your partners
          </div>
        )}
        {brands.length > 0 && shown.length === 0 && (
          <div className="bg-white rounded-lg border border-gray-200 py-10 text-center text-xs text-gray-400">
            {/* The filter can empty the grid with no search term at all —
                every unsponsored brand could have been set aside. */}
            {search
              ? <>No brands match “{search}”</>
              : <>Every unsponsored brand was excluded in review. Use “Show {hiddenCount} excluded” to see them.</>}
          </div>
        )}
        {brands.length > 0 && (
          <div className="mt-3">
            {/* Counts the brands, not the cards on screen: the KPI row above is
                deliberately over the whole broadcast, and a pager that implied
                otherwise would make the two disagree. */}
            <BroadcastPagination
              page={page}
              perPage={PER_PAGE}
              total={shown.length}
              unfilteredTotal={brands.length}
              noun="brands"
              onPage={setPage}
            />
          </div>
        )}
      </div>

      <BroadcastBrandDrawer
        brand={drilled}
        // From the WHOLE broadcast, sponsors included: folding a stray
        // spelling into its sponsor is the main reason to merge from here.
        // Only brands that count — merging into an excluded row or a club
        // mark is a mistake the list should not offer.
        // Every brand not set aside, sponsors included. Unsponsored brands
        // are offered too: two spellings of a club mark or an unsigned brand
        // are worth folding together here, before anyone sells against them.
        candidates={allBrands.filter((b) => b.excluded_reason === null).map((b) => ({
          brand_key: b.brand_key,
          brand_name: b.display_name ?? b.brand_name,
          is_sponsor: b.is_sponsored
        }))}
        onMakeSponsor={(brand) => { setDrilled(null); setSponsorFrom(brand) }}
        customerId={customerId}
        broadcastId={id ?? ''}
        source={customerReviewSource}
        onZoneChanged={() => { void reload() }}
        onClose={() => setDrilled(null)}
      />

      <AddPartnerDrawer
        isOpen={sponsorFrom !== null}
        onClose={() => setSponsorFrom(null)}
        initialName={sponsorFrom?.display_name ?? sponsorFrom?.brand_name}
        // Every spelling this row stands for: its key, the name shown, any
        // merged keys and the raw model readings. The server dedupes.
        brandAliases={sponsorFrom ? [...new Set([
          sponsorFrom.brand_name,
          ...(sponsorFrom.merged_keys ?? []),
          ...(sponsorFrom.raw_names ?? [])
        ].filter(Boolean))] : []}
        onCreated={async (p) => {
          showSuccess('Sponsor added',
            `${p.name} now counts as a sponsor and appears on Sponsor Exposure.`)
          await reload()
        }}
      />
    </div>
  )
}

export default BroadcastBrandsPage
